import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import './ReservationsPage.css';
import { adminHeaders } from '../../lib/adminAuth';
import OtaReservationForm from '../../components/admin/OtaReservationForm';
import IssuedUrlPanel from '../../components/admin/IssuedUrlPanel';
import BookingImportModal from '../../components/admin/BookingImportModal';
import AirbnbImportModal from '../../components/admin/AirbnbImportModal';
import { EMPTY_OTA_FORM } from '../../components/admin/otaTypes';
import type { OtaFormState, IssuedInfo } from '../../components/admin/otaTypes';

import { tx } from '../../lib/adminI18n';
interface Reservation {
  id: string;
  property_name: string;
  guest_name: string;
  guest_email: string;
  checkin_date: string;
  checkout_date: string;
  total_price: number;
  status: string;
  created_at: string;
  guest_count?: number;
  free_cancel_until_days?: number;
  passport_received?: number;
  cancel_requested?: number;
  payment_method?: string | null;
  stripe_session_id?: string | null;
  cancel_request_reason?: string | null;
  cancel_requested_at?: string | null;
  source_channel?: string | null;
  access_code?: string | null;        // 表示用の短い予約番号（SD-YYYYMMDD-NNNNNN）
  ota_reservation_code?: string | null;
  review_reason?: string | null;
  cancel_suspicion?: string | null;   // 'RULE|CONFIDENCE|STREAK'（OPEN のときだけ）
  prev_checkin_date?: string | null;
  prev_checkout_date?: string | null;
  [key: `tag_${string}`]: number | undefined;
}

// 複数ステータス（タグ）。サーバー側で tag_xxx = 1/0 として返る
const TAGS: { key: string; label: string; manual?: boolean }[] = [
  { key: 'approved', get label() { return tx('rsv.0'); } },
  { key: 'confirmed', get label() { return tx('rsv.1'); } },
  { key: 'cancel_requested', get label() { return tx('rsv.2'); }, manual: true },
  { key: 'cancelled', get label() { return tx('rsv.3'); } },
  { key: 'passport', get label() { return tx('rsv.4'); }, manual: true },
  { key: 'checkin_today', get label() { return tx('rsv.5'); } },
  { key: 'staying', get label() { return tx('rsv.6'); } },
  { key: 'checkout_today', get label() { return tx('rsv.7'); } },
  { key: 'finished', get label() { return tx('rsv.8'); } },
  { key: 'info_incomplete', get label() { return tx('rsv.otaIncomplete'); } },
  { key: 'cancel_suspected', get label() { return tx('rsv.otaCancelSuspected'); } },
  { key: 'active_in_feed', get label() { return tx('rsv.otaActiveInFeed'); } },
  { key: 'ota_review', get label() { return tx('rsv.otaReview'); } },
  { key: 'date_changed', get label() { return tx('rsv.otaDateChanged'); } },
];
// OTA同期のタグ → 確認ページのタブ
const OTA_TAB: Record<string, string> = { info_incomplete: 'incomplete', cancel_suspected: 'review', active_in_feed: 'review', ota_review: 'review', date_changed: 'dates' };
const CHANNEL_LABEL: Record<string, string> = { airbnb: 'Airbnb', booking: 'Booking.com' };
// 'RULE|CONFIDENCE|STREAK' → ツールチップ文言
const suspicionTitle = (v: string) => {
  const [rule, conf, streak] = String(v).split('|');
  type TxKey = Parameters<typeof tx>[0];
  const k = (v: string) => `rsv.cs_${v}` as TxKey;   // 動的キー（rule / confidence）を型付きキーとして扱う
  return `${tx(k(rule))}（${tx('rsv.csConfidence')}: ${tx(k(conf))} / ${tx('rsv.csStreak', { p0: streak })}）`;
};
const activeTags = (r: Reservation) => TAGS.filter((t) => Number((r as any)[`tag_${t.key}`]) === 1);

function ReservationsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [sortKey, setSortKey] = useState<string>(searchParams.get('sort') || 'checkin_asc');
  const [propertyFilter, setPropertyFilter] = useState<string>(searchParams.get('property') || '');
  const [tagFilter, setTagFilter] = useState<string[]>((searchParams.get('tags') || '').split(',').filter(Boolean));
  const [propertyOptions, setPropertyOptions] = useState<{ id: string; name: string }[]>([]);
  // 過去（チェックアウト日が過ぎた）予約を表示するか。既定は非表示。
  const [showPast, setShowPast] = useState<boolean>(searchParams.get('past') === '1');
  // キャンセル済み・返金済みの予約を表示するか。既定は非表示。
  const [showCancelled, setShowCancelled] = useState<boolean>(searchParams.get('cancelled') === '1');

  // 物件一覧（フィルター用）
  useEffect(() => {
    fetch('/api/admin/properties', { headers: adminHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d: any) => {
        const list = Array.isArray(d) ? d : d?.properties || [];
        setPropertyOptions(list.map((p: any) => ({ id: p.id, name: p.name })));
      })
      .catch(() => setPropertyOptions([]));
  }, []);

  // URL のクエリを現在の絞り込み・並び順に合わせる
  const syncParams = (o: { status?: string; sort?: string; property?: string; tags?: string[]; past?: boolean; cancelled?: boolean }) => {
    const tg = o.tags ?? tagFilter;
    const st = o.status ?? statusFilter;
    const so = o.sort ?? sortKey;
    const pr = o.property ?? propertyFilter;
    const pa = o.past ?? showPast;
    const ca = o.cancelled ?? showCancelled;
    const next: Record<string, string> = {};
    if (st) next.status = st;
    if (pr) next.property = pr;
    if (tg.length) next.tags = tg.join(',');
    if (so !== 'checkin_asc') next.sort = so;
    if (pa) next.past = '1';
    if (ca) next.cancelled = '1';
    setSearchParams(next);
  };
  const [selected, setSelected] = useState<Reservation | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const highlightId = searchParams.get('highlight');

  useEffect(() => {
    fetchReservations();
  }, [statusFilter, sortKey, propertyFilter, tagFilter.join(',')]);

  // highlight パラメータがあれば、該当予約の詳細モーダルを自動で開く
  useEffect(() => {
    if (!highlightId || reservations.length === 0) return;
    const target = reservations.find((r) => r.id === highlightId);
    if (target) {
      setActionError(null);
      setSelected(target);
    }
  }, [highlightId, reservations]);

  const [listError, setListError] = useState<string | null>(null);

  // OTA予約の手動登録 + URL発行（パスポート／チェックイン画面と同じバックエンド /api/admin/passport/* を再利用）
  const [showOtaForm, setShowOtaForm] = useState(false);
  const [otaMsg, setOtaMsg] = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');
  const [otaForm, setOtaForm] = useState<OtaFormState>({ ...EMPTY_OTA_FORM });
  const [issued, setIssued] = useState<IssuedInfo | null>(null);
  const [showBookingImport, setShowBookingImport] = useState(false);
  const [showAirbnbImport, setShowAirbnbImport] = useState(false);

  async function copyUrl(text: string) {
    try { await navigator.clipboard.writeText(text); setCopiedUrl(text); setTimeout(() => setCopiedUrl(''), 2000); }
    catch { /* クリップボード不可環境では手動選択 */ }
  }

  // OTA予約を登録 → 固定URL + 予約番号 + チェックイン日を発行して表示（メールは送らない）。
  async function submitOta(e: React.FormEvent) {
    e.preventDefault();
    setOtaMsg(''); setIssued(null);
    const res = await fetch('/api/admin/passport/ota-reservations', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(otaForm),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setOtaMsg(d.error || tx('rsv.9')); return; }
    setIssued({ reservationId: d.reservation_id, isOta: true, url: d.fixed_url, code: d.access_code, checkin: d.checkin_date });
    setOtaMsg(tx('rsv.10'));
    setShowOtaForm(false);
    setOtaForm({ ...EMPTY_OTA_FORM });
    await fetchReservations();
  }

  const fetchReservations = async () => {
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      params.append('sort', sortKey);
      if (propertyFilter) params.append('property_id', propertyFilter);
      if (tagFilter.length) params.append('tags', tagFilter.join(','));

      const response = await fetch(`/api/admin/reservations?${params}`, {
        headers: adminHeaders(),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({} as any));
        const detail = err?.detail || err?.error || '';
        throw new Error(
          response.status === 401
            ? tx('rsv.11')
            : tx('rsv.12', { p0: response.status, p1: detail ? `: ${detail}` : '' })
        );
      }

      const data = await response.json();
      const list: Reservation[] = data.reservations || [];
      setReservations(list);
      // 詳細モーダルを開いたままなら最新の内容に差し替える
      setSelected((cur) => (cur ? list.find((x) => x.id === cur.id) || cur : cur));
    } catch (err: any) {
      console.error('Reservations error:', err);
      setReservations([]);
      setListError(
        err instanceof TypeError
          ? tx('rsv.13')
          : err?.message || tx('rsv.14')
      );
    } finally {
      setLoading(false);
    }
  };

  // 予約に対する操作（キャンセル / 入金確認）を実行する
  const runAction = async (path: string, confirmMsg: string) => {
    if (!selected) return;
    if (!window.confirm(confirmMsg)) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const response = await fetch(`/api/admin/reservations/${selected.id}/${path}`, {
        method: 'PATCH',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || tx('rsv.15', { p0: response.status }));
      }
      // 成功 → 返金結果があれば知らせてから、モーダルを閉じて一覧を再取得
      const data = await response.json().catch(() => ({} as any));
      if (path === 'cancel') {
        window.alert((data.refunded
          ? tx('rsv.16')
          : tx('rsv.17', { p0: data.refund_note ? '\n' + data.refund_note : '' }))
          + (data.mail_sent ? tx('rsv.18') : tx('rsv.19')));
      }
      setSelected(null);
      await fetchReservations();
    } catch (err: any) {
      setActionError(err?.message || String(err));
    } finally {
      setActionLoading(false);
    }
  };

  // 手動フラグ（キャンセル申請 / パスポート受領済）の切り替え
  const toggleFlag = async (tagKey: string, on: boolean) => {
    if (!selected) return;
    const [path, body] = tagKey === 'passport'
      ? ['passport', { received: on }]
      : ['cancel-request', { requested: on }];
    setActionLoading(true);
    setActionError(null);
    try {
      const response = await fetch(`/api/admin/reservations/${selected.id}/${path}`, {
        method: 'PATCH',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || tx('rsv.20', { p0: response.status }));
      }
      await fetchReservations();
    } catch (err: any) {
      setActionError(err?.message || String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const toggleTagFilter = (key: string) => {
    const next = tagFilter.includes(key) ? tagFilter.filter((k) => k !== key) : [...tagFilter, key];
    setTagFilter(next);
    syncParams({ tags: next });
  };

  const renderTags = (r: Reservation) => {
    let tags = activeTags(r);
    // 本日チェックイン・宿泊中・本日チェックアウト・終了 はいずれも「確定」が前提条件。
    // これらの具体的な状態が付く予約では「予約確定」バッジは自明なので隠す。
    const impliesConfirmed = ['checkin_today', 'staying', 'checkout_today', 'finished'];
    if (tags.some((t) => impliesConfirmed.includes(t.key))) {
      tags = tags.filter((t) => t.key !== 'confirmed');
    }
    return (
      <div className="tag-list">
        {tags.map((t) => (
          <span key={t.key} className={`status-tag tag-${t.key}`}
            title={(t.key === 'cancel_suspected' || t.key === 'active_in_feed') && r.cancel_suspicion ? suspicionTitle(r.cancel_suspicion)
              : t.key === 'ota_review' && r.review_reason ? tx(r.review_reason === 'OVERLAP' ? 'rsv.otaOverlap' : 'rsv.otaMissing')
              : t.key === 'date_changed' && r.prev_checkin_date ? `${r.prev_checkin_date} → ${r.prev_checkout_date}` : undefined}>
            {t.label}
          </span>
        ))}
        {r.source_channel && CHANNEL_LABEL[r.source_channel] && <span className="status-tag tag-ota">{CHANNEL_LABEL[r.source_channel]}</span>}
        {r.status === 'PAID_EXPIRED' && <span className="status-tag tag-alert">{getStatusLabel(r.status)}</span>}
        {r.status === 'EXPIRED' && <span className="status-tag tag-muted">{getStatusLabel(r.status)}</span>}
      </div>
    );
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      'PENDING_PAYMENT': tx('rsv.21'),
      'PENDING_TRANSFER': tx('rsv.22'),
      'CONFIRMED': tx('rsv.23'),
      'CANCELLED': tx('rsv.24'),
      'EXPIRED': tx('rsv.25'),
      'REFUNDED': tx('rsv.26'),
      'PAID_EXPIRED': tx('rsv.27'),
    };
    return statusMap[status] || status;
  };

  // 無料キャンセル期限日を算出: チェックイン日 − free_cancel_until_days 日（既定30）
  const freeCancelUntil = (r: Reservation): string => {
    const days = r.free_cancel_until_days ?? 30;
    const d = new Date(r.checkin_date);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  // 表示用リスト（画面上の「表示絞り込み」で制御）:
  //   ・既定ではチェックアウト日が過ぎた予約を隠す（「過去も表示」ONで解除）
  //   ・既定ではキャンセル済み・返金済みを隠す（「キャンセル済みも表示」ONで解除）
  //     ただしステータスで CANCELLED/REFUNDED を明示選択中は常に表示する。
  const todayJST = new Date(Date.now() + 9 * 3600 * 1000).toISOString().split('T')[0];
  const cancelStatusSelected = statusFilter === 'CANCELLED' || statusFilter === 'REFUNDED';
  const visibleReservations = reservations.filter((r) => {
    if (!showPast && r.checkout_date < todayJST) return false;
    if (!showCancelled && !cancelStatusSelected && ['CANCELLED', 'REFUNDED'].includes(r.status)) return false;
    return true;
  });

  return (
    <div className="reservations-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <h1>{t('admin.reservations')}</h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="pp-btn" onClick={() => setShowBookingImport(true)}>
            {tx('rsv.28')}
          </button>
          <button className="pp-btn" onClick={() => setShowAirbnbImport(true)}>
            {tx('rsv.29')}
          </button>
          <button className="pp-btn" onClick={() => { setShowOtaForm((v) => !v); setIssued(null); setOtaMsg(''); }}>
            {showOtaForm ? tx('rsv.30') : tx('rsv.31')}
          </button>
        </div>
      </div>

      {showBookingImport && (
        <BookingImportModal
          properties={propertyOptions}
          onClose={() => setShowBookingImport(false)}
          onImported={() => fetchReservations()}
        />
      )}

      {showAirbnbImport && (
        <AirbnbImportModal
          onClose={() => setShowAirbnbImport(false)}
          onImported={() => fetchReservations()}
        />
      )}

      {otaMsg && <p style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '.6rem .8rem', borderRadius: 8 }}>{otaMsg}</p>}

      {/* URL発行結果パネル / OTA予約登録フォーム（3画面共通コンポーネント） */}
      {issued && (
        <div style={{ margin: '0 0 1rem' }}>
          <IssuedUrlPanel issued={issued} copiedUrl={copiedUrl} onCopy={copyUrl} onClose={() => setIssued(null)} />
        </div>
      )}
      {showOtaForm && (
        <OtaReservationForm form={otaForm} properties={propertyOptions} onChange={setOtaForm} onSubmit={submitOta} />
      )}

      {listError && (
        <div className="list-error" role="alert">
          <strong>⚠ {listError}</strong>
          <button type="button" onClick={() => fetchReservations()}>{tx('rsv.32')}</button>
        </div>
      )}

      <div className="filters">
        <select
          value={statusFilter ? statusFilter : (tagFilter.length === 1 ? `tag:${tagFilter[0]}` : '')}
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith('tag:')) {
              // 計算タグ（宿泊中・本日チェックイン等）: status をクリアして tags フィルターとして送信
              const key = v.slice(4);
              setStatusFilter('');
              setTagFilter([key]);
              syncParams({ status: '', tags: [key] });
            } else {
              // 通常の予約ステータス: tags をクリアして status フィルターとして送信
              setStatusFilter(v);
              setTagFilter([]);
              syncParams({ status: v, tags: [] });
            }
          }}
          className="status-filter"
        >
          <option value="">{tx('rsv.33')}</option>
          <option value="PENDING_PAYMENT">{tx('rsv.21')}</option>
          <option value="PENDING_TRANSFER">{tx('rsv.22')}</option>
          <option value="CONFIRMED">{tx('rsv.23')}</option>
          <option value="CANCELLED">{tx('rsv.24')}</option>
          <option value="PAID_EXPIRED">{tx('rsv.27')}</option>
          <option value="EXPIRED">{tx('rsv.25')}</option>
          <option value="REFUNDED">{tx('rsv.26')}</option>
          <option value="tag:checkin_today">{tx('rsv.5')}</option>
          <option value="tag:staying">{tx('rsv.6')}</option>
          <option value="tag:checkout_today">{tx('rsv.7')}</option>
          <option value="tag:passport">{tx('rsv.4')}</option>
          <option value="tag:info_incomplete">{tx('rsv.otaIncomplete')}</option>
          <option value="tag:ota_review">{tx('rsv.otaReview')}</option>
          <option value="tag:date_changed">{tx('rsv.otaDateChanged')}</option>
        </select>
        <select
          value={propertyFilter}
          onChange={(e) => {
            const v = e.target.value;
            setPropertyFilter(v);
            syncParams({ property: v });
          }}
          className="status-filter"
          title={tx('rsv.34')}
        >
          <option value="">{tx('rsv.35')}</option>
          {propertyOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={sortKey}
          onChange={(e) => {
            const v = e.target.value;
            setSortKey(v);
            syncParams({ sort: v });
          }}
          className="status-filter sort-select"
          title={tx('rsv.36')}
        >
          <option value="created_desc">{tx('rsv.37')}</option>
          <option value="checkin_asc">{tx('rsv.38')}</option>
          <option value="checkin_desc">{tx('rsv.39')}</option>
        </select>
      </div>

      {/* 表示絞り込み（表示する予約の範囲を切り替える） */}
      <div className="display-filters" role="group" aria-label={tx('rsv.40')}>
        <span className="display-filters-label">{tx('rsv.41')}</span>
        <label className="display-check" title={tx('rsv.42')}>
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => {
              const v = e.target.checked;
              setShowPast(v);
              syncParams({ past: v });
            }}
          />
          {tx('rsv.43')}
        </label>
        <label className="display-check" title={tx('rsv.44')}>
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => {
              const v = e.target.checked;
              setShowCancelled(v);
              syncParams({ cancelled: v });
            }}
          />
          {tx('rsv.45')}
        </label>
      </div>

      <div className="tag-filters" role="group" aria-label={tx('rsv.46')}>
        <span className="tag-filters-label">{tx('rsv.47')}</span>
        {TAGS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tag-chip tag-${t.key} ${tagFilter.includes(t.key) ? 'active' : ''}`}
            onClick={() => toggleTagFilter(t.key)}
            aria-pressed={tagFilter.includes(t.key)}
          >
            {t.label}
          </button>
        ))}
        {tagFilter.length > 0 && (
          <button type="button" className="tag-chip-clear" onClick={() => { setTagFilter([]); syncParams({ tags: [] }); }}>
            {tx('rsv.48')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div className="reservations-table-container">
          <table className="reservations-table">
            <thead>
              <tr>
                <th>{tx('rsv.49')}</th>
                <th
                  className="sortable-th"
                  onClick={() => {
                    const v = sortKey === 'checkin_asc' ? 'checkin_desc' : 'checkin_asc';
                    setSortKey(v);
                    syncParams({ sort: v });
                  }}
                  title={tx('rsv.50')}
                >
                  {tx('rsv.51')}{sortKey === 'checkin_asc' ? ' ▲' : sortKey === 'checkin_desc' ? ' ▼' : ' ⇅'}
                </th>
                <th>{tx('rsv.52')}</th>
                <th>{tx('rsv.53')}</th>
                <th>{tx('rsv.54')}</th>
                <th>{tx('rsv.55')}</th>
                <th>{tx('rsv.56')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleReservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td data-label={tx('rsv.49')}>{reservation.property_name}</td>
                  <td data-label={tx('rsv.51')}>{reservation.checkin_date}</td>
                  <td data-label={tx('rsv.52')}>{reservation.checkout_date}</td>
                  <td data-label={tx('rsv.53')}>{reservation.guest_count != null ? tx('rsv.57', { p0: reservation.guest_count }) : '-'}</td>
                  <td data-label={tx('rsv.54')}>{freeCancelUntil(reservation)}</td>
                  <td data-label={tx('rsv.55')}>{renderTags(reservation)}</td>
                  <td data-label={tx('rsv.56')} className="col-action">
                    <button
                      className="btn-small"
                      onClick={() => { setActionError(null); setSelected(reservation); }}
                    >
                      {tx('rsv.58')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {visibleReservations.length === 0 && (
            <div className="no-data">{tx('rsv.59')}</div>
          )}
        </div>
      )}

      {/* 予約詳細モーダル */}
      {selected && (
        <div className="modal-overlay" onClick={() => !actionLoading && setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{tx('rsv.60')}</h2>
              <button
                className="modal-close"
                onClick={() => setSelected(null)}
                disabled={actionLoading}
                aria-label={tx('rsv.30')}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <dl className="detail-list">
                <dt>{tx('rsv.61')}</dt>
                {/* 短い予約番号を優先表示。未発番の旧予約のみ UUID。内部IDは小さく併記 */}
                <dd className="reservation-id">
                  {selected.access_code || selected.id}
                  {selected.access_code && (
                    <div className="muted" style={{ fontSize: '.75rem', wordBreak: 'break-all' }}>ID: {selected.id}</div>
                  )}
                </dd>
                <dt>{tx('rsv.49')}</dt>
                <dd>{selected.property_name}</dd>
                <dt>{tx('rsv.62')}</dt>
                <dd>{selected.guest_name || <span className="muted">{tx('rsv.otaUnset')}</span>}</dd>
                <dt>{tx('rsv.63')}</dt>
                <dd>{selected.guest_email}</dd>
                <dt>{tx('rsv.53')}</dt>
                <dd>{selected.guest_count != null ? tx('rsv.57', { p0: selected.guest_count }) : '-'}</dd>
                <dt>{tx('rsv.51')}</dt>
                <dd>{selected.checkin_date}</dd>
                <dt>{tx('rsv.52')}</dt>
                <dd>{selected.checkout_date}</dd>
                <dt>{tx('rsv.64')}</dt>
                <dd className="amount">¥{selected.total_price.toLocaleString()}</dd>
                {selected.ota_reservation_code && <>
                  <dt>{tx('rsv.otaCode')}</dt>
                  <dd>{selected.ota_reservation_code}</dd>
                </>}
                {Number(selected.tag_cancel_suspected) === 1 && selected.cancel_suspicion && <>
                  <dt>{tx('rsv.otaCancelSuspected')}</dt>
                  <dd className="cancel-suspect-note">⚠ {suspicionTitle(selected.cancel_suspicion)}</dd>
                </>}
                {Number(selected.tag_date_changed) === 1 && selected.prev_checkin_date && <>
                  <dt>{tx('rsv.otaDateChanged')}</dt>
                  <dd><s>{selected.prev_checkin_date} → {selected.prev_checkout_date}</s> ⇒ {selected.checkin_date} → {selected.checkout_date}</dd>
                </>}
                <dt>{tx('rsv.55')}</dt>
                <dd>
                  {activeTags(selected).filter((t) => OTA_TAB[t.key]).map((t) => (
                    <div key={t.key} className="ota-detail-note">
                      <Link to={`/admin/calendar?view=ota&tab=${OTA_TAB[t.key]}`}>{t.label}：{tx('rsv.otaGoReview')} →</Link>
                    </div>
                  ))}
                  {renderTags(selected)}
                  <small className="raw-status">{tx('rsv.65')} {getStatusLabel(selected.status)}</small>
                </dd>
                <dt>{tx('rsv.66')}</dt>
                <dd className="manual-flags">
                  <label>
                    <input
                      type="checkbox"
                      checked={Number(selected.passport_received) === 1}
                      disabled={actionLoading}
                      onChange={(e) => toggleFlag('passport', e.target.checked)}
                    />
                    {tx('rsv.4')}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={Number(selected.cancel_requested) === 1}
                      disabled={actionLoading || ['CANCELLED', 'REFUNDED'].includes(selected.status)}
                      onChange={(e) => toggleFlag('cancel_requested', e.target.checked)}
                    />
                    {tx('rsv.67')}
                  </label>
                  {Number(selected.cancel_requested) === 1 && (selected.cancel_requested_at || selected.cancel_request_reason) && (
                    <div className="cancel-request-info">
                      {selected.cancel_requested_at && <div>{tx('rsv.68')} {selected.cancel_requested_at}{tx('rsv.69')}</div>}
                      {selected.cancel_request_reason && <div>{tx('rsv.70')} {selected.cancel_request_reason}</div>}
                    </div>
                  )}
                </dd>
                <dt>{tx('rsv.24')}</dt>
                <dd>
                  {freeCancelUntil(selected)} {tx('rsv.71')}{selected.free_cancel_until_days ?? 30}{tx('rsv.72')}
                  <br />
                  <small>{tx('rsv.73')}</small>
                </dd>
              </dl>

              {actionError && <div className="action-error">{actionError}</div>}
            </div>

            <div className="modal-footer">
              {selected.status === 'PENDING_PAYMENT' && (
                <button
                  className="btn btn-confirm"
                  disabled={actionLoading}
                  onClick={() => runAction('confirm-payment', tx('rsv.74'))}
                >
                  {tx('rsv.75')}
                </button>
              )}
              {/* 期限切れ後に決済 → 再確保して確定 / 返金 */}
              {selected.status === 'PAID_EXPIRED' && (
                <>
                  <button
                    className="btn btn-confirm"
                    disabled={actionLoading}
                    onClick={() => runAction('retry-confirm', tx('rsv.76'))}
                  >
                    {tx('rsv.77')}
                  </button>
                  <button
                    className="btn btn-cancel"
                    disabled={actionLoading}
                    onClick={() => runAction('refund', tx('rsv.78'))}
                  >
                    {tx('rsv.79')}
                  </button>
                </>
              )}
              {/* 振込待ち → 入金確認して確定 */}
              {selected.status === 'PENDING_TRANSFER' && (
                <button
                  className="btn btn-confirm"
                  disabled={actionLoading}
                  onClick={() => runAction('confirm-transfer', tx('rsv.80'))}
                >
                  {tx('rsv.81')}
                </button>
              )}
              {/* 確定・振込待ち → キャンセル可能 */}
              {(selected.status === 'CONFIRMED' || selected.status === 'PENDING_TRANSFER' || selected.status === 'PENDING_PAYMENT') && (
                <button
                  className="btn btn-cancel"
                  disabled={actionLoading}
                  onClick={() => {
                    const autoRefund = selected.status === 'CONFIRMED' && !!selected.stripe_session_id &&
                      new Date().toISOString().split('T')[0] <= freeCancelUntil(selected);
                    runAction('cancel', autoRefund
                      ? tx('rsv.82')
                      : tx('rsv.83'));
                  }}
                >
                  {Number(selected.cancel_requested) === 1 ? tx('rsv.84') : tx('rsv.85')}
                </button>
              )}
              <button
                className="btn btn-confirm"
                disabled={actionLoading}
                onClick={() => { window.location.href = `/admin/messages/${encodeURIComponent((selected.guest_email || '').trim().toLowerCase())}`; }}
              >
                {tx('rsv.86')}
              </button>
              <button
                className="btn btn-close"
                disabled={actionLoading}
                onClick={() => setSelected(null)}
              >
                {tx('rsv.30')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReservationsPage;
